import type { LoginSuccessResponse, User } from '@/types/auth'
import type { FormError } from '@/types/httpError'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { refreshTokenApiCall } from '@/api/login'
import { useApiCall } from '@/composables/useApiCall'
import {
  getStorageItemWithExpiry,
  setStorageItemWithExpiry,
} from '@/helpers/localStorageHelpers'

const authToken = getStorageItemWithExpiry<string>('authToken')

export const useAuthStore = defineStore('auth', () => {
  const token = ref(authToken)
  const tokenType = ref<string | null>(getStorageItemWithExpiry(`tokenType`))
  const user = ref<User | null>(getStorageItemWithExpiry(`user`))
  const isAuth = computed(() => token.value)
  const logout = () => {
    token.value = null
    tokenType.value = null
    user.value = null
    localStorage.clear()
  }
  const {
    isLoading,
    data: refreshResponse,
    error: refreshError,
    executeApiCall: refreshAction,
  } = useApiCall<LoginSuccessResponse, FormError>(
    refreshTokenApiCall,
    true,
  )

  const isRefreshLoading = computed(() => isLoading.value)
  const refreshToken = async () => {
    try {
      await refreshAction()
      if (refreshResponse.value) {
        token.value = refreshResponse.value.accessToken
        user.value = refreshResponse.value.user
        tokenType.value = refreshResponse.value.tokenType
        setStorageItemWithExpiry('authToken', token.value)
        setStorageItemWithExpiry(`user`, user.value)
        setStorageItemWithExpiry(`tokenType`, tokenType.value)
      }
    }
    catch {
      if (refreshError.value) {
        console.error(refreshError.value)
      }
    }
  }

  return {
    token,
    tokenType,
    user,
    isAuth,
    logout,
    refreshToken,
    isRefreshLoading,
  }
})
